import { Controller } from "@hotwired/stimulus"

// Controls interactive behavior on the AiHunt-style profile edit page
export default class extends Controller {
  static targets = [
    "modal",
    "actionsMenu",
    "actionsButton",
    "tab",
    "tabContent",
    "coverContainer",
    "coverImage",
    "coverPositionForm",
    "coverPositionX",
    "coverPositionY",
    "aboutBio",
    "aboutToggle"
  ]

  connect() {
    super.connect()

    console.log('Profile Edit Controller connected')
    console.log('Actions button:', this.hasActionsButtonTarget ? this.actionsButtonTarget : 'NOT FOUND')
    console.log('Actions menu:', this.hasActionsMenuTarget ? this.actionsMenuTarget : 'NOT FOUND')
    console.log('Modal target:', this.hasModalTarget ? this.modalTarget : 'NOT FOUND')

    // Simple direct event setup
    this.setupDirectEventListeners()

    // Set up file input listeners directly
    this.setupFileInputs()

    // Cover reposition state
    this.isCoverDragging = false
    this.coverDragStart = null
    this.coverPointerId = null

    // About read-more toggle setup
    this.setupAboutBioToggle()

    this.applyInitialTabFromUrl()

    this.availableSkills = [
      "react",
      "javascript",
      "typescript",
      "python",
      "rust",
      "solidity",
      "golang",
      "java",
      "node",
      "vue",
      "angular",
      "next.js",
      "react-native",
      "flutter",
      "swift",
      "kotlin",
      "php",
      "ruby",
      "scala",
      "c-sharp",
      "c-plus-plus",
      "haskell",
      "clojure",
      "ethereum",
      "bitcoin",
      "blockchain",
      "smart-contract",
      "defi",
      "nft",
      "dao",
      "web3",
      "solana",
      "polkadot",
      "tezos",
      "cosmos",
      "avalanche",
      "polygon",
      "arbitrum",
      "aws",
      "docker",
      "kubernetes",
      "terraform",
      "jenkins",
      "git",
      "linux",
      "devops",
      "postgresql",
      "mysql",
      "mongodb",
      "redis",
      "graphql",
      "rest-api",
      "microservices",
      "machine-learning",
      "ai",
      "data-science",
      "tensorflow",
      "pytorch",
      "pandas",
      "design",
      "ui-ux",
      "figma",
      "sketch",
      "photoshop",
      "illustrator",
      "marketing",
      "seo",
      "content-marketing",
      "social-media",
      "copywriting",
      "product-manager",
      "project-manager",
      "scrum",
      "agile",
      "kanban"
    ]

    this.closeOnEsc = this.closeOnEsc.bind(this)
    this.handleOutsideActions = this.handleOutsideActions.bind(this)

    if (!this.hasAlertsContainerTarget) {
      this.alertIndexValue = 0
    }

    // Add direct event listeners for close buttons to ensure they work
    // Use delegation on document to handle dynamically added modals
    this.closeButtonHandler = (e) => {
      const closeBtn = e.target.closest('.profile-modal__close')
      if (closeBtn) {
        console.log("Close button clicked via direct listener")
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        const modal = closeBtn.closest('.profile-modal')
        if (modal) {
          modal.classList.remove("is-visible")
          document.body.classList.remove("profile-edit-modal-open")
          document.removeEventListener("keydown", this.closeOnEsc)
          console.log("Modal closed via direct listener")
        }
      }
    }
    document.addEventListener('click', this.closeButtonHandler, true) // Use capture phase

    // Add form submit handler to debug alerts deletion
    this.setupFormSubmitHandler()
  }

  applyInitialTabFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const tabParam = params.get("tab") || window.location.hash.replace("#", "")

    if (!tabParam || !this.hasTabTarget) return

    const targetTab = this.tabTargets.find(tab => tab.dataset.tab === tabParam)
    if (!targetTab) return

    const syntheticEvent = { currentTarget: targetTab }
    this.switchTab(syntheticEvent)
  }

  // === Cover image reposition (drag to change object-position) ===
  startCoverDrag(event) {
    // Do not start dragging when interacting with action buttons/menus
    if (event.target.closest(".profile-hero__actions, .profile-hero__cover-actions")) return
    if (!this.hasCoverImageTarget || !this.hasCoverContainerTarget) return
    if (!this.hasCoverPositionFormTarget || !this.hasCoverPositionXTarget || !this.hasCoverPositionYTarget) return

    // Only allow dragging when cover image exists (still OK for default image, but keep consistent)
    event.preventDefault()

    const point = this._pointerPoint(event)
    if (!point) return

    this.isCoverDragging = true
    this.coverContainerTarget.classList.add("is-repositioning")

    const startX = parseFloat(this.coverPositionXTarget.value || "50")
    const startY = parseFloat(this.coverPositionYTarget.value || "50")

    this.coverDragStart = {
      x: point.x,
      y: point.y,
      startX,
      startY
    }

    // Track touch pointer to avoid multi-touch issues
    if (event.pointerId != null) this.coverPointerId = event.pointerId

    window.addEventListener("mousemove", this._onCoverDragMove, true)
    window.addEventListener("mouseup", this._onCoverDragEnd, true)
    window.addEventListener("touchmove", this._onCoverDragMove, { passive: false, capture: true })
    window.addEventListener("touchend", this._onCoverDragEnd, true)
  }

  _onCoverDragMove = (event) => {
    if (!this.isCoverDragging || !this.coverDragStart) return
    if (event.pointerId != null && this.coverPointerId != null && event.pointerId !== this.coverPointerId) return

    const point = this._pointerPoint(event)
    if (!point) return

    event.preventDefault()

    const rect = this.coverContainerTarget.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const dx = point.x - this.coverDragStart.x
    const dy = point.y - this.coverDragStart.y

    const newX = this._clamp(this.coverDragStart.startX + (dx / rect.width) * 100, 0, 100)
    const newY = this._clamp(this.coverDragStart.startY + (dy / rect.height) * 100, 0, 100)

    this.coverPositionXTarget.value = newX.toFixed(2)
    this.coverPositionYTarget.value = newY.toFixed(2)

    // Apply instantly for preview
    if (this.hasCoverImageTarget) {
      this.coverImageTarget.style.objectPosition = `${newX}% ${newY}%`
    }
  }

  _onCoverDragEnd = (event) => {
    if (!this.isCoverDragging) return
    if (event && event.pointerId != null && this.coverPointerId != null && event.pointerId !== this.coverPointerId) return

    this.isCoverDragging = false
    this.coverPointerId = null
    this.coverDragStart = null

    if (this.hasCoverContainerTarget) {
      this.coverContainerTarget.classList.remove("is-repositioning")
    }

    window.removeEventListener("mousemove", this._onCoverDragMove, true)
    window.removeEventListener("mouseup", this._onCoverDragEnd, true)
    window.removeEventListener("touchmove", this._onCoverDragMove, true)
    window.removeEventListener("touchend", this._onCoverDragEnd, true)

    // Persist the new position via standard Rails form submit (no AJAX dependency)
    if (this.hasCoverPositionFormTarget) {
      this.coverPositionFormTarget.requestSubmit()
    }
  }

  _pointerPoint(event) {
    if (event.touches && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY }
    }
    if (typeof event.clientX === "number" && typeof event.clientY === "number") {
      return { x: event.clientX, y: event.clientY }
    }
    return null
  }

  _clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
  }

  setupFormSubmitHandler() {
    // Find all forms that might contain alerts
    const forms = document.querySelectorAll('form[action*="/users"]')
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        const alertsContainer = form.querySelector('[data-profile-edit-target="alertsContainer"]')
        if (alertsContainer) {
          console.log('=== FORM SUBMIT DEBUG: Alerts Data ===')
          const formData = new FormData(form)
          const alertsData = {}

          // Collect all alerts_attributes
          for (const [key, value] of formData.entries()) {
            if (key.includes('alerts_attributes')) {
              alertsData[key] = value
              console.log(`Alert field: ${key} = ${value}`)
            }
          }

          // Verify _destroy fields
          const destroyFields = alertsContainer.querySelectorAll('input[name*="_destroy"]')
          console.log(`Found ${destroyFields.length} _destroy fields:`)
          destroyFields.forEach(field => {
            console.log(`  - ${field.name} = ${field.value} (display: ${field.closest('[data-alert-item]')?.style.display || 'visible'})`)
          })

          console.log('=== END FORM SUBMIT DEBUG ===')
        }
      })
    })
  }

  disconnect() {
    this.removeActionListeners()
    // Remove the close button listener
    if (this.closeButtonHandler) {
      document.removeEventListener('click', this.closeButtonHandler, true)
    }
  }

  // === Modal handling ===
  openModal(event) {
    event.preventDefault()
    event.stopPropagation()
    this.closeActions()

    // KILL THE PROFILER - it blocks button clicks!
    document.querySelectorAll('.profiler-button, .profiler-totals, [class*="profiler"]').forEach(el => {
      el.remove()
    })

    const modalName = event.currentTarget.dataset.modal

    console.log('Looking for modal:', modalName);

    if (!modalName) {
      console.error("No modal name found on", event.currentTarget)
      return
    }

    // Use querySelector to find modal within controller scope or document
    const modal = this.element.querySelector(`[data-profile-edit-target="modal"][data-modal="${modalName}"]`) ||
      document.querySelector(`[data-profile-edit-target="modal"][data-modal="${modalName}"]`)

    console.log('Found modal:', modal);

    if (modal) {
      modal.classList.add("is-visible")
      document.body.classList.add("profile-edit-modal-open")
      document.addEventListener("keydown", this.closeOnEsc)

      // Focus management - trap focus within modal
      this.previousActiveElement = document.activeElement
      this.trapFocus(modal)

      // Set up backup click listener for THIS SPECIFIC MODAL
      this.setupBackupClickHandler(modal)

      // Set up file inputs when resume modal opens
      if (modalName === "resume") {
        setTimeout(() => this.setupFileInputs(), 100)
      }

      // Initialize Trix editor when bio modal opens
      if (modalName === "bio") {
        setTimeout(() => this.initializeTrixEditor(modal), 100)
      }

      // Set up direct event listeners for skills modal
      if (modalName === "skills") {
        setTimeout(() => {
          this.setupSkillsModalListeners(modal)
        }, 100)
      }

      // Set up avatar dropzone when profile-picture modal opens
      if (modalName === "profile-picture") {
        setTimeout(() => this.setupAvatarDropzone(), 100)
      }
    } else {
      console.error("Modal not found:", modalName)
    }
  }

  closeModal(event) {
    console.log("closeModal called", event)

    // KILL THE PROFILER AGAIN - in case it came back
    document.querySelectorAll('.profiler-button, .profiler-totals, [class*="profiler"]').forEach(el => {
      el.remove()
    })

    if (event) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }

    // Find the modal - check if clicked element is inside a modal
    let modal = null
    if (event && event.currentTarget) {
      modal = event.currentTarget.closest(".profile-modal")
    }
    if (!modal && event && event.target) {
      modal = event.target.closest(".profile-modal")
    }

    // If not found, try to find any visible modal
    if (!modal) {
      modal = document.querySelector('.profile-modal.is-visible')
    }

    console.log("Found modal:", modal)

    if (modal) {
      modal.classList.remove("is-visible")
      document.body.classList.remove("profile-edit-modal-open")
      document.removeEventListener("keydown", this.closeOnEsc)

      // Focus management - restore focus to previous element
      this.removeFocusTrap(modal)
      if (this.previousActiveElement) {
        this.previousActiveElement.focus()
      }

      console.log("Modal closed successfully")
    } else {
      // Close all modals as fallback
      const allModals = document.querySelectorAll('.profile-modal')
      allModals.forEach((target) => target.classList.remove("is-visible"))
      document.body.classList.remove("profile-edit-modal-open")
      document.removeEventListener("keydown", this.closeOnEsc)

      // Focus management - restore focus to previous element
      this.removeFocusTrap(null)
      if (this.previousActiveElement) {
        this.previousActiveElement.focus()
      }

      console.log("Closed all modals as fallback")
    }
  }

  handleDialogClick(event) {
    // Allow all clicks within modal dialog - only handle close actions
    console.log('Dialog click:', event.target);

    // Only handle close actions specifically — scope to profile-edit's own
    // closeModal action so foreign controllers' closeModal handlers (e.g.
    // linkedin-import#closeModal on the LinkedIn modal) aren't swallowed.
    const isCloseAction = event.target.closest('[data-action*="profile-edit#closeModal"]');
    if (isCloseAction) {
      console.log('Close action detected, closing modal');
      event.preventDefault();
      event.stopPropagation();
      this.closeModal(event);
      return;
    }

    // Allow all other clicks to work normally (links, buttons, etc.)
    console.log('Click allowed - not a close action');
    return;
  }

  handleBackdropClick(event) {
    // Only close if clicking directly on the backdrop (not the dialog)
    if (event.target.classList.contains("profile-modal")) {
      event.preventDefault()
      event.stopPropagation()
      this.closeModal(event)
    }
  }

  closeOnEsc(event) {
    if (event.key === "Escape") {
      this.closeModal()
    }
  }

  stopPropagation(event) {
    // Don't stop propagation if clicking the close button
    if (event.target.closest('.profile-modal__close')) {
      return
    }
    event.stopPropagation()
  }

  // === Actions dropdown ===
  toggleActions(event) {
    console.log('toggleActions called', event)
    event.preventDefault()
    event.stopPropagation()

    // BACKUP: Try Stimulus targets first
    let menu = null
    let button = null

    if (this.hasActionsMenuTarget) {
      menu = this.actionsMenuTarget
      button = this.actionsButtonTarget
      console.log('Using Stimulus targets')
    } else {
      // BACKUP: Find elements directly
      menu = document.querySelector('[data-profile-edit-target="actionsMenu"]')
      button = document.querySelector('[data-profile-edit-target="actionsButton"]')
      console.log('Using direct DOM selection')
      console.log('Found menu:', menu)
      console.log('Found button:', button)
    }

    if (!menu) {
      console.warn('Actions menu target not found')
      return
    }

    const willOpen = !menu.classList.contains("is-open")
    console.log('Will open:', willOpen, 'Menu:', menu)

    this.closeActions()

    if (willOpen) {
      menu.classList.add("is-open")
      document.addEventListener("click", this.handleOutsideActions)
      document.addEventListener("keydown", this.handleOutsideActions)
      console.log('Actions menu opened')
    }
  }

  handleOutsideActions = (event) => {
    if (event.type === "keydown" && event.key !== "Escape") return
    if (event.type === "click") {
      // BACKUP: Try Stimulus targets first, then direct DOM
      let menu = null
      let button = null

      if (this.hasActionsMenuTarget) {
        menu = this.actionsMenuTarget
        button = this.actionsButtonTarget
      } else {
        menu = document.querySelector('[data-profile-edit-target="actionsMenu"]')
        button = document.querySelector('[data-profile-edit-target="actionsButton"]')
      }

      const clickInsideMenu = menu && menu.contains(event.target)
      const clickOnButton = button && button.contains(event.target)
      if (clickInsideMenu || clickOnButton) return
    }
    this.closeActions()
  }

  closeActions() {
    // BACKUP: Try Stimulus targets first, then direct DOM
    let menu = null

    if (this.hasActionsMenuTarget) {
      menu = this.actionsMenuTarget
    } else {
      menu = document.querySelector('[data-profile-edit-target="actionsMenu"]')
    }

    if (menu) {
      menu.classList.remove("is-open")
    }
    this.removeActionListeners()
  }

  removeActionListeners() {
    document.removeEventListener("click", this.handleOutsideActions)
    document.removeEventListener("keydown", this.handleOutsideActions)
  }

  copyProfileUrl(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.profileUrl
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        // Show a temporary success message
        const button = event.currentTarget
        const originalText = button.querySelector('span').textContent
        button.querySelector('span').textContent = 'Copied!'
        setTimeout(() => {
          button.querySelector('span').textContent = originalText
        }, 2000)
      }).catch(err => {
        console.error('Failed to copy URL:', err)
      })
    }
  }

  openLinkedInImport(event) {
    event.preventDefault()
    event.stopPropagation()
    this.closeActions()

    // Find the LinkedIn import modal
    const modal = document.querySelector('[data-linkedin-import-modal]')
    if (modal) {
      // Show the modal
      modal.classList.add('is-visible')
      document.body.classList.add('linkedin-import-modal-open')

      // Get the controller and trigger import
      const controller = this.application.getControllerForElementAndIdentifier(modal, 'linkedin-import')
      if (controller) {
        controller.openLinkedInImport(event)
      } else {
        // If controller not found, try to initialize it
        console.warn('LinkedIn import controller not found, trying to initialize...')
        // The controller should auto-initialize via Stimulus
        setTimeout(() => {
          const retryController = this.application.getControllerForElementAndIdentifier(modal, 'linkedin-import')
          if (retryController) {
            retryController.openLinkedInImport(event)
          }
        }, 100)
      }
    } else {
      console.error('LinkedIn import modal not found')
    }
  }

  changeCoverImage(event) {
    event.preventDefault()
    this.closeActions()
    // Trigger the hidden file input
    const input = document.getElementById('cover-image-input')
    if (input) {
      input.click()
    }
  }

  uploadCoverImage(event) {
    const file = event.target.files[0]
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        alert('Please select an image file')
        event.target.value = '' // Clear the input
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB')
        event.target.value = '' // Clear the input
        return
      }

      // Submit the form
      const form = document.getElementById('cover-image-form')
      if (form) {
        form.submit()
      }
    }
  }

  autoSubmit(event) {
    const form = event.target.closest('form')
    if (!form) return

    clearTimeout(this.autoSubmitTimeout)
    this.autoSubmitTimeout = setTimeout(() => {
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit()
      } else {
        form.submit()
      }
    }, 250)
  }

  setupAboutBioToggle() {
    if (!this.hasAboutBioTarget || !this.hasAboutToggleTarget) return

    requestAnimationFrame(() => {
      const bio = this.aboutBioTarget
      const toggle = this.aboutToggleTarget

      if (bio.scrollHeight <= bio.clientHeight + 4) {
        toggle.style.display = "none"
      }
    })
  }

  toggleAboutBio(event) {
    event.preventDefault()
    if (!this.hasAboutBioTarget || !this.hasAboutToggleTarget) return

    const bio = this.aboutBioTarget
    const toggle = this.aboutToggleTarget
    const isExpanded = bio.classList.toggle("is-expanded")

    bio.classList.toggle("is-collapsed", !isExpanded)
    toggle.textContent = isExpanded ? "Read less" : "Read more"
    toggle.setAttribute("aria-expanded", String(isExpanded))
  }

  // === Skills ===
  addSkill(event) {
    event.preventDefault()
    event.stopPropagation()

    // Find targets using document.querySelector as fallback
    const skillInput = this.hasSkillInputTarget ? this.skillInputTarget : document.querySelector('[data-profile-edit-target="skillInput"]')
    const skillsTags = this.hasSkillsTagsTarget ? this.skillsTagsTarget : document.querySelector('[data-profile-edit-target="skillsTags"]')

    console.log("addSkill called", {
      hasSkillInputTarget: this.hasSkillInputTarget,
      hasSkillsTagsTarget: this.hasSkillsTagsTarget,
      skillInputFound: !!skillInput,
      skillsTagsFound: !!skillsTags,
      skillInputValue: skillInput ? skillInput.value : "N/A"
    })

    if (!skillInput || !skillsTags) {
      console.error("Missing targets:", {
        skillInput: !!skillInput,
        skillsTags: !!skillsTags
      })
      alert("Error: Could not find skill input or tags container. Please refresh the page.")
      return
    }

    const rawValue = skillInput.value.trim().toLowerCase().replace(/\s+/g, "-")
    if (!rawValue) {
      console.log("Empty skill value")
      return
    }

    console.log("Adding skill:", rawValue)
    this.appendSkillTag(rawValue, skillsTags)
    skillInput.value = ""
    this.hideSkillsAutocomplete()
  }

  selectSkill(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value
    if (!value) return
    this.appendSkillTag(value)
    this.skillInputTarget.value = ""
    this.hideSkillsAutocomplete()
  }

  removeSkill(event) {
    event.preventDefault()
    event.stopPropagation()

    console.log("removeSkill called", event.currentTarget)
    const tag = event.currentTarget.closest(".skill-tag-modal")
    console.log("Found tag:", tag)
    if (tag) {
      tag.remove()
      console.log("Tag removed")
    } else {
      console.error("Could not find .skill-tag-modal parent")
    }
  }

  searchSkills(event) {
    if (!this.hasSkillsAutocompleteTarget) return
    const query = event.target.value.trim().toLowerCase()

    if (!query) {
      this.hideSkillsAutocomplete()
      return
    }

    const matches = this.availableSkills.filter((skill) => skill.includes(query)).slice(0, 8)
    if (matches.length === 0) {
      this.hideSkillsAutocomplete()
      return
    }

    this.skillsAutocompleteTarget.innerHTML = matches
      .map(
        (skill) => `
          <button type="button" class="autocomplete-item" data-value="${skill}" data-action="profile-edit#selectSkill">
            ${this.formatLabel(skill)}
          </button>
        `
      )
      .join("")

    this.skillsAutocompleteTarget.classList.add("is-visible")
  }

  hideSkillsAutocomplete() {
    if (this.hasSkillsAutocompleteTarget) {
      this.skillsAutocompleteTarget.classList.remove("is-visible")
      this.skillsAutocompleteTarget.innerHTML = ""
    }
  }

  appendSkillTag(skill, container = null) {
    console.log("appendSkillTag called with:", skill)

    const skillsTags = container || (this.hasSkillsTagsTarget ? this.skillsTagsTarget : document.querySelector('[data-profile-edit-target="skillsTags"]'))

    if (!skillsTags) {
      console.error("skillsTagsTarget not found!")
      return
    }

    console.log("skillsTagsTarget found:", skillsTags)

    const existingValues = Array.from(skillsTags.querySelectorAll("input[name='user[tags][]']")).map(
      (input) => input.value
    )
    console.log("Existing skills:", existingValues)

    if (existingValues.includes(skill)) {
      console.log("Skill already exists:", skill)
      return
    }

    const wrapper = document.createElement("div")
    wrapper.className = "skill-tag-modal"
    wrapper.innerHTML = `
      <span>${this.formatLabel(skill)}</span>
      <button type="button" class="skill-tag-modal-remove" data-action="profile-edit#removeSkill" aria-label="Remove skill">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6L6 18M6 6l12 12"></path>
        </svg>
      </button>
      <input type="hidden" name="user[tags][]" value="${skill}">
    `
    skillsTags.appendChild(wrapper)
    console.log("Skill tag appended successfully")
  }

  // === Languages ===
  addLanguage(event) {
    event.preventDefault()
    event.stopPropagation()

    // Find targets using document.querySelector as fallback
    const languageInput = this.hasLanguageInputTarget ? this.languageInputTarget : document.querySelector('[data-profile-edit-target="languageInput"]')
    const languageTags = this.hasLanguageTagsTarget ? this.languageTagsTarget : document.querySelector('[data-profile-edit-target="languageTags"]')

    console.log("addLanguage called", {
      hasLanguageInputTarget: this.hasLanguageInputTarget,
      hasLanguageTagsTarget: this.hasLanguageTagsTarget,
      languageInputFound: !!languageInput,
      languageTagsFound: !!languageTags,
      languageInputValue: languageInput ? languageInput.value : "N/A"
    })

    if (!languageInput || !languageTags) {
      console.error("Missing targets:", {
        languageInput: !!languageInput,
        languageTags: !!languageTags
      })
      alert("Error: Could not find language input or tags container. Please refresh the page.")
      return
    }

    const language = languageInput.value.trim()
    if (!language) {
      console.log("Empty language value")
      return
    }

    const normalized = language.toLowerCase()
    const existingValues = Array.from(
      languageTags.querySelectorAll("input[name='user[spoken_languages][]']")
    ).map((input) => input.value.toLowerCase())

    if (existingValues.includes(normalized)) {
      console.log("Language already exists:", normalized)
      languageInput.value = ""
      return
    }

    console.log("Adding language:", language)
    const wrapper = document.createElement("div")
    wrapper.className = "language-tag-modal"
    wrapper.innerHTML = `
      <div class="language-tag-content">
        <span class="language-tag-name">${this.formatLabel(language)}</span>
        <span class="language-tag-level">Fluent</span>
      </div>
      <button type="button" class="language-tag-modal-remove" data-action="profile-edit#removeLanguage" aria-label="Remove language">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6L6 18M6 6l12 12"></path>
        </svg>
      </button>
      <input type="hidden" name="user[spoken_languages][]" value="${language}">
    `
    languageTags.appendChild(wrapper)
    languageInput.value = ""
    console.log("Language added successfully")
  }

  removeLanguage(event) {
    event.preventDefault()
    event.stopPropagation()

    console.log("removeLanguage called", event.currentTarget)
    const tag = event.currentTarget.closest(".language-tag-modal")
    console.log("Found tag:", tag)
    if (tag) {
      tag.remove()
      console.log("Tag removed")
    } else {
      console.error("Could not find .language-tag-modal parent")
    }
  }

  // === Job alerts ===
  addAlert(event) {
    console.log('addAlert called', event)
    event.preventDefault()

    // KILL THE PROFILER
    document.querySelectorAll('.profiler-button, .profiler-totals, [class*="profiler"]').forEach(el => {
      el.remove()
    })

    // Find container directly in DOM (don't rely on Stimulus targets)
    const alertsContainer = document.querySelector('[data-profile-edit-target="alertsContainer"]')

    if (!alertsContainer) {
      console.error('alertsContainer element not found in DOM!')
      alert('Error: Could not find alerts container. Please refresh the page and try again.')
      return
    }

    console.log('Found alertsContainer:', alertsContainer)

    // Check if user is premium
    const addButton = event.currentTarget
    const isPaid = addButton.dataset.userPaid === 'true'
    console.log('User is paid:', isPaid)

    // Count existing alerts (excluding those marked for deletion)
    const existingAlerts = Array.from(alertsContainer.querySelectorAll('[data-alert-item]'))
      .filter(item => {
        // Skip if hidden and marked for destruction
        if (item.style.display === 'none' && item.hasAttribute('data-marked-for-destruction')) {
          return false
        }
        // Skip if _destroy field is set to 1
        const destroyField = item.querySelector('input[name*="_destroy"]')
        if (destroyField && destroyField.value === '1') {
          return false
        }
        return true
      })
      .length

    // Check alert limit for free users
    if (!isPaid && existingAlerts >= 1) {
      this.showUpgradePrompt()
      return
    }

    // Calculate next index based on all existing items (including those marked for deletion)
    const allItems = alertsContainer.querySelectorAll('[data-alert-item]')
    const index = this.alertIndexValue || allItems.length

    let frequencyOptions = []
    const optionsData = addButton.dataset.alertFrequencyOptions
    if (optionsData) {
      try {
        frequencyOptions = JSON.parse(optionsData)
      } catch (error) {
        console.warn('Invalid alert frequency options JSON', error)
      }
    }

    if (!Array.isArray(frequencyOptions) || frequencyOptions.length === 0) {
      frequencyOptions = [
        { value: 'immediately', label: 'Immediately' },
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' }
      ]
    }

    const normalizedOptions = frequencyOptions.map(option => {
      if (typeof option === 'string') {
        return { value: option, label: option.replace(/^\w/, char => char.toUpperCase()) }
      }
      return option
    })

    const defaultFrequency = normalizedOptions.find(option => option.value === 'daily')?.value ||
      normalizedOptions[0]?.value || ''
    const frequencyOptionsMarkup = [
      { value: '', label: 'Select frequency' },
      ...normalizedOptions
    ].map(option => {
      const selected = option.value === defaultFrequency ? ' selected' : ''
      return `<option value="${option.value}"${selected}>${option.label}</option>`
    }).join('')

    const alertRow = document.createElement("div")
    alertRow.className = "alert-item-modal"
    alertRow.dataset.alertItem = ""
    alertRow.innerHTML = `
      <div class="alert-item-modal__fields">
        <div class="alert-item-modal__field">
          <label class="alert-item-modal__label">Keyword (Job Title / Skill / Technology)</label>
          <input type="text" name="user[alerts_attributes][${index}][tag]" class="profile-modal__input" placeholder="e.g. React, DevOps, Product Manager, Python" value="">
          <p class="profile-modal__hint" style="margin-top: 4px;">This is the main keyword to match jobs</p>
        </div>
        <div class="alert-item-modal__field">
          <label class="alert-item-modal__label">Location Preference</label>
          <input type="text" name="user[alerts_attributes][${index}][location]" class="profile-modal__input" placeholder="e.g. Remote, San Francisco, New York" value="">
          <p class="profile-modal__hint" style="margin-top: 4px;">Leave blank or use "Remote" for remote-only jobs</p>
        </div>
        <div class="alert-item-modal__field">
          <label class="alert-item-modal__label">Alert Frequency</label>
          <select name="user[alerts_attributes][${index}][how_often]" class="profile-modal__input">
            ${frequencyOptionsMarkup}
          </select>
        </div>
      </div>
      <button type="button" class="alert-item-modal__remove" data-action="profile-edit#removeAlert" title="Remove this alert">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6h18"></path>
          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
          <line x1="10" x2="10" y1="11" y2="17"></line>
          <line x1="14" x2="14" y1="11" y2="17"></line>
        </svg>
      </button>
    `

    // Remove empty state message if it exists
    const emptyMessage = alertsContainer.querySelector('.alert-items-modal__empty')
    if (emptyMessage) {
      emptyMessage.remove()
    }

    alertsContainer.appendChild(alertRow)
    this.alertIndexValue = index + 1
    this.updateAlertCount()

    console.log('Alert form added successfully. Total alerts:', alertsContainer.querySelectorAll('[data-alert-item]').length)

    // Focus on the first input field
    const firstInput = alertRow.querySelector('input[type="text"]')
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100)
    }
  }

  removeAlert(event) {
    event.preventDefault()
    event.stopPropagation()

    console.log('removeAlert called', event)

    const row = event.currentTarget.closest("[data-alert-item], .alert-item-modal")
    if (!row) {
      console.error('Could not find alert row')
      return
    }

    console.log('Found alert row:', row)

    // Check if this is an existing alert (has an ID field) or a new one
    const idField = row.querySelector('input[name*="[id]"]')

    console.log('ID field:', idField, idField?.value, idField?.name)

    if (idField && idField.value) {
      // Existing alert - mark for deletion instead of removing
      // Find the _destroy field - it should be a sibling of the id field
      let destroyField = row.querySelector('input[name*="_destroy"]')

      // If not found, try to find it by looking for hidden fields with _destroy in name
      if (!destroyField) {
        const allHiddenFields = row.querySelectorAll('input[type="hidden"]')
        allHiddenFields.forEach(field => {
          if (field.name && field.name.includes('_destroy')) {
            destroyField = field
          }
        })
      }

      // If still not found, create it
      if (!destroyField) {
        console.log('Creating _destroy field')
        destroyField = this.createDestroyField(row, idField.name)
      }

      console.log('Found/created _destroy field:', destroyField.name, 'Current value:', destroyField.value)
      destroyField.value = '1'
      console.log('Set _destroy to 1')

      // Ensure the field is not disabled and is included in form submission
      destroyField.disabled = false
      destroyField.removeAttribute('disabled')

      // Ensure ID field is also enabled
      idField.disabled = false
      idField.removeAttribute('disabled')

      // Hide the row visually but keep it in DOM so form submission includes it
      // Use a class instead of inline style to ensure fields remain accessible
      row.classList.add('alert-marked-for-deletion')
      row.style.display = 'none'
      row.setAttribute('data-marked-for-destruction', 'true')

      // Verify the field value was set
      console.log('Verification - _destroy field value:', destroyField.value)
      console.log('Alert marked for deletion')
    } else {
      // New alert - just remove from DOM
      console.log('Removing new alert from DOM')
      row.remove()
    }

    this.updateAlertCount()

    // Show empty message if no visible alerts left
    const alertsContainer = document.querySelector('[data-profile-edit-target="alertsContainer"]')
    if (alertsContainer) {
      const visibleAlerts = Array.from(alertsContainer.querySelectorAll('[data-alert-item]'))
        .filter(item => item.style.display !== 'none' && !item.hasAttribute('data-marked-for-destruction'))
        .length

      if (visibleAlerts === 0 && !alertsContainer.querySelector('.alert-items-modal__empty')) {
        const emptyMessage = document.createElement('p')
        emptyMessage.className = 'alert-items-modal__empty'
        emptyMessage.textContent = 'No custom alerts configured yet. Add your first alert below.'
        alertsContainer.appendChild(emptyMessage)
      }
    }
  }

  createDestroyField(row, idFieldName) {
    // Create hidden _destroy field for marking existing records for deletion
    const destroyField = document.createElement('input')
    destroyField.type = 'hidden'
    // Convert alerts[0][id] to alerts[0][_destroy]
    // Handle both formats: user[alerts_attributes][0][id] -> user[alerts_attributes][0][_destroy]
    destroyField.name = idFieldName.replace(/\[id\]$/, '[_destroy]').replace(/\[id\]/, '[_destroy]')
    destroyField.value = '1' // Set to 1 immediately since we're creating it for deletion
    destroyField.setAttribute('data-destroy-field', 'true')

    // Insert it right after the ID field for consistency
    const idField = row.querySelector('input[name*="[id]"]')
    if (idField && idField.parentNode) {
      idField.parentNode.insertBefore(destroyField, idField.nextSibling)
    } else {
      row.appendChild(destroyField)
    }

    console.log('Created _destroy field with name:', destroyField.name)
    return destroyField
  }

  updateAlertCount() {
    // Update alert count badge if it exists
    const countBadges = document.querySelectorAll('.alert-count-badge')
    const alertsContainer = document.querySelector('[data-profile-edit-target="alertsContainer"]')

    if (countBadges.length > 0 && alertsContainer) {
      // Only count visible alerts (not marked for deletion)
      const alertCount = Array.from(alertsContainer.querySelectorAll('[data-alert-item]'))
        .filter(item => {
          // Skip if hidden and marked for destruction
          if (item.style.display === 'none' && item.hasAttribute('data-marked-for-destruction')) {
            return false
          }
          // Skip if _destroy field is set to 1
          const destroyField = item.querySelector('input[name*="_destroy"]')
          if (destroyField && destroyField.value === '1') {
            return false
          }
          return true
        })
        .length

      countBadges.forEach(badge => {
        if (badge.classList.contains('alert-count-badge--free')) {
          badge.textContent = `${alertCount}/1 alert`
        } else {
          badge.textContent = `${alertCount} alert${alertCount !== 1 ? 's' : ''}`
        }
      })
    }
  }

  checkAlertLimit(isPaid, currentCount) {
    if (isPaid) return true
    return currentCount < 1
  }

  showUpgradePrompt() {
    alert('You\'ve reached the free plan limit of 1 custom alert. Upgrade to Premium for unlimited custom alerts!')
  }

  // === Avatar Preview ===
  previewAvatar(event) {
    const input = event.target
    const file = input.files?.[0]

    if (!file) return

    this.processAvatarFile(file, input)
  }

  processAvatarFile(file, input = null) {
    // Validate file type
    if (!file.type.match('image.*')) {
      alert('Please select an image file (PNG, JPG, or GIF)')
      if (input) input.value = ''
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB')
      if (input) input.value = ''
      return
    }

    // Update preview image
    const reader = new FileReader()
    reader.onload = (e) => {
      // Find preview elements
      const previewContainer = document.querySelector('[data-profile-edit-target="avatarPreview"]')
      if (previewContainer) {
        const img = previewContainer.querySelector('img')
        if (img) {
          img.src = e.target.result
        }
      }

      // Update dropzone state
      const dropzone = document.querySelector('[data-profile-edit-target="avatarDropzone"]')
      if (dropzone) {
        dropzone.classList.add('has-file')
      }

      // Update filename text
      const fileNameEl = document.querySelector('[data-profile-edit-target="avatarFileName"]')
      if (fileNameEl) {
        fileNameEl.textContent = file.name
      }
    }
    reader.readAsDataURL(file)
  }

  // Setup drag and drop for avatar upload when modal opens
  setupAvatarDropzone() {
    const dropzone = document.querySelector('[data-profile-edit-target="avatarDropzone"]')
    const input = document.querySelector('[data-profile-edit-target="avatarInput"]')

    if (!dropzone || !input) return

      // Prevent default drag behaviors
      ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        dropzone.addEventListener(eventName, (e) => {
          e.preventDefault()
          e.stopPropagation()
        }, false)
      })

      // Highlight drop zone when dragging over
      ;['dragenter', 'dragover'].forEach(eventName => {
        dropzone.addEventListener(eventName, () => {
          dropzone.classList.add('is-dragover')
        }, false)
      })

      ;['dragleave', 'drop'].forEach(eventName => {
        dropzone.addEventListener(eventName, () => {
          dropzone.classList.remove('is-dragover')
        }, false)
      })

    // Handle dropped files
    dropzone.addEventListener('drop', (e) => {
      const files = e.dataTransfer.files
      if (files.length > 0) {
        // Set the file to the input
        const dataTransfer = new DataTransfer()
        dataTransfer.items.add(files[0])
        input.files = dataTransfer.files

        // Process the file
        this.processAvatarFile(files[0], input)
      }
    }, false)
  }

  // === File input label ===
  setupFileInputs() {
    // Set up direct event listeners for file inputs
    // Search in document since modal might be outside controller scope
    const fileInputs = document.querySelectorAll('.resume-file-input')
    console.log('Setting up file inputs, found:', fileInputs.length)
    fileInputs.forEach(input => {
      // Remove existing listeners to avoid duplicates by cloning
      const newInput = input.cloneNode(true)
      input.parentNode.replaceChild(newInput, input)
      // Add new listener
      newInput.addEventListener('change', (e) => {
        console.log('File input change event fired')
        this.updateFileLabel(e)
      })
      console.log('Added change listener to input:', newInput)
    })
  }

  updateFileLabel(event) {
    const input = event.target

    console.log('File input changed:', {
      files: input.files?.length || 0,
      fileName: input.files?.[0]?.name || 'none'
    })

    // Find the label element directly by traversing DOM (most reliable)
    const label = input.closest('label.resume-file-upload-label')
    if (!label) {
      console.error('Could not find label element for input:', input)
      return
    }

    const textElement = label.querySelector('.resume-file-upload-text')
    const hintElement = label.querySelector('.resume-file-upload-hint')

    if (input.files && input.files.length > 0) {
      const fileName = input.files[0].name

      console.log('Updating label with filename:', fileName)

      // Update hint to show filename
      if (hintElement) {
        hintElement.textContent = fileName
        console.log('Updated hint element:', hintElement.textContent)
      } else {
        console.error('Hint element not found')
      }

      // Update text element to show filename
      if (textElement) {
        textElement.textContent = fileName
        console.log('Updated text element:', textElement.textContent)
      } else {
        console.error('Text element not found')
      }

      label.classList.add("has-file")
      console.log('Added has-file class to label')
    } else {
      // Reset to default state
      const defaultText = textElement?.getAttribute('data-default-text') || "Choose File"

      if (textElement) {
        textElement.textContent = defaultText
      }

      if (hintElement) {
        hintElement.textContent = "No file chosen"
      }

      label.classList.remove("has-file")
    }
  }

  // === Resume Viewer ===
  openResumeViewer(event) {
    // Don't open if clicking on delete button
    if (event.target.closest('.resume-delete-btn')) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const resumeUrl = event.currentTarget.dataset.resumeUrl
    if (!resumeUrl) {
      console.error('No resume URL found')
      return
    }

    console.log('Opening resume viewer with URL:', resumeUrl)

    // Find the resume viewer modal
    const modal = document.querySelector('[data-profile-edit-target="modal"][data-modal="resume-viewer"]')
    if (!modal) {
      console.error('Resume viewer modal not found')
      return
    }

    // Set the iframe source
    const iframe = modal.querySelector('#resume-viewer-iframe')
    if (iframe) {
      iframe.src = resumeUrl
    }

    // Open the modal
    modal.classList.add("is-visible")
    document.body.classList.add("profile-edit-modal-open")
    document.addEventListener("keydown", this.closeOnEsc)
  }

  stopPropagation(event) {
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  // === Trix Editor Initialization ===
  initializeTrixEditor(modal) {
    // Wait a bit for the modal to be fully visible
    setTimeout(() => {
      // Find all trix-editor elements in the modal
      const trixEditors = modal.querySelectorAll('trix-editor')

      trixEditors.forEach(editor => {
        // Check if Trix is available
        if (typeof Trix !== 'undefined') {
          // Find the associated hidden input
          const input = editor.previousElementSibling
          if (input && input.tagName === 'INPUT' && input.type === 'hidden') {
            // Check if editor is already initialized
            if (!editor.editor) {
              // Trix should auto-initialize, but trigger it manually if needed
              // Dispatch trix-initialize event to ensure initialization
              const initEvent = new Event('trix-initialize', { bubbles: true, cancelable: true })
              editor.dispatchEvent(initEvent)

              // Force focus to ensure editor is ready
              setTimeout(() => {
                if (editor.editor) {
                  editor.focus()
                }
              }, 50)
            }
          }
        } else {
          console.warn('Trix is not loaded. Make sure trix is included in your assets.')
        }
      })
    }, 150)
  }

  // === Delete Education ===
  deleteEducation(event) {
    event.preventDefault()
    event.stopPropagation()

    const educationId = event.currentTarget.dataset.educationId
    if (!educationId) {
      console.error('No education ID found')
      return
    }

    if (!confirm('Are you sure you want to delete this education?')) {
      return
    }

    const url = `/educations/${educationId}`
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin'
    })
      .then(response => {
        if (response.ok) {
          // Reload the page to show updated list
          window.location.reload()
        } else {
          alert('Failed to delete education. Please try again.')
        }
      })
      .catch(error => {
        console.error('Error deleting education:', error)
        alert('An error occurred while deleting the education.')
      })
  }

  // === Delete Employment ===
  deleteEmployment(event) {
    event.preventDefault()
    event.stopPropagation()

    const employmentId = event.currentTarget.dataset.employmentId
    if (!employmentId) {
      console.error('No employment ID found')
      return
    }

    if (!confirm('Are you sure you want to delete this experience?')) {
      return
    }

    const url = `/employments/${employmentId}`
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin'
    })
      .then(response => {
        if (response.ok) {
          // Reload the page to show updated list
          window.location.reload()
        } else {
          alert('Failed to delete experience. Please try again.')
        }
      })
      .catch(error => {
        console.error('Error deleting employment:', error)
        alert('An error occurred while deleting the experience.')
      })
  }

  // === Skills Modal Direct Listeners ===
  setupSkillsModalListeners(modal) {
    console.log("=== Setting up skills modal listeners ===")

    // Store modal reference for use in handlers
    this.skillsModal = modal

    // Find Add skill button - it's in the skills input group
    const skillsInputGroup = modal.querySelector('.skills-modal-input-group')
    if (skillsInputGroup) {
      const addSkillBtn = skillsInputGroup.querySelector('button')
      console.log("Add skill button found:", addSkillBtn)
      if (addSkillBtn) {
        // Remove all existing handlers by cloning without onclick
        const newBtn = addSkillBtn.cloneNode(false)
        newBtn.textContent = addSkillBtn.textContent
        newBtn.className = addSkillBtn.className
        newBtn.type = addSkillBtn.type
        Array.from(addSkillBtn.attributes).forEach(attr => {
          if (attr.name !== 'onclick') {
            newBtn.setAttribute(attr.name, attr.value)
          }
        })
        addSkillBtn.parentNode.replaceChild(newBtn, addSkillBtn)

        newBtn.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          console.log("=== ADD SKILL BUTTON CLICKED ===")
          this.addSkillDirect(e, modal)
          return false
        }
        console.log("✓ Add skill listener attached")
      } else {
        console.error("✗ Add skill button NOT found!")
      }
    }

    // Find Add language button
    const languageField = modal.querySelector('[data-profile-edit-target="languageTags"]')?.closest('.profile-modal__field')
    if (languageField) {
      const languageInputGroup = languageField.querySelector('.skills-modal-input-group')
      if (languageInputGroup) {
        const addLanguageBtn = languageInputGroup.querySelector('button')
        console.log("Add language button found:", addLanguageBtn)
        if (addLanguageBtn) {
          const newBtn = addLanguageBtn.cloneNode(false)
          newBtn.textContent = addLanguageBtn.textContent
          newBtn.className = addLanguageBtn.className
          newBtn.type = addLanguageBtn.type
          Array.from(addLanguageBtn.attributes).forEach(attr => {
            if (attr.name !== 'onclick') {
              newBtn.setAttribute(attr.name, attr.value)
            }
          })
          addLanguageBtn.parentNode.replaceChild(newBtn, addLanguageBtn)

          newBtn.onclick = (e) => {
            e.preventDefault()
            e.stopPropagation()
            e.stopImmediatePropagation()
            console.log("=== ADD LANGUAGE BUTTON CLICKED ===")
            this.addLanguageDirect(e, modal)
            return false
          }
          console.log("✓ Add language listener attached")
        } else {
          console.error("✗ Add language button NOT found!")
        }
      }
    }

    // Remove buttons - use event delegation (capture phase to catch early)
    modal.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.skill-tag-modal-remove, .language-tag-modal-remove')
      if (removeBtn) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        console.log("=== REMOVE BUTTON CLICKED ===", removeBtn)
        const tag = removeBtn.closest('.skill-tag-modal, .language-tag-modal')
        if (tag) {
          tag.remove()
          console.log("✓ Tag removed successfully")
        } else {
          console.error("✗ Could not find tag parent")
        }
        return false
      }
    }, true) // Use capture phase

    console.log("=== Setup complete ===")
  }

  // Direct add skill method (bypasses Stimulus targets)
  addSkillDirect(event, modal) {
    event.preventDefault()
    event.stopPropagation()

    const skillInput = modal.querySelector('[data-profile-edit-target="skillInput"]')
    const skillsTags = modal.querySelector('[data-profile-edit-target="skillsTags"]')

    console.log("addSkillDirect called", {
      skillInput: !!skillInput,
      skillsTags: !!skillsTags,
      value: skillInput ? skillInput.value : "N/A"
    })

    if (!skillInput || !skillsTags) {
      console.error("Missing elements:", { skillInput: !!skillInput, skillsTags: !!skillsTags })
      alert("Error: Could not find skill input or tags container.")
      return
    }

    const rawValue = skillInput.value.trim().toLowerCase().replace(/\s+/g, "-")
    if (!rawValue) {
      console.log("Empty skill value")
      return
    }

    console.log("Adding skill:", rawValue)
    this.appendSkillTagDirect(rawValue, skillsTags)
    skillInput.value = ""

    // Hide autocomplete
    const autocomplete = modal.querySelector('[data-profile-edit-target="skillsAutocomplete"]')
    if (autocomplete) {
      autocomplete.classList.remove("is-visible")
      autocomplete.innerHTML = ""
    }
  }

  // Direct add language method (bypasses Stimulus targets)
  addLanguageDirect(event, modal) {
    event.preventDefault()
    event.stopPropagation()

    const languageInput = modal.querySelector('[data-profile-edit-target="languageInput"]')
    const languageTags = modal.querySelector('[data-profile-edit-target="languageTags"]')

    console.log("addLanguageDirect called", {
      languageInput: !!languageInput,
      languageTags: !!languageTags,
      value: languageInput ? languageInput.value : "N/A"
    })

    if (!languageInput || !languageTags) {
      console.error("Missing elements:", { languageInput: !!languageInput, languageTags: !!languageTags })
      alert("Error: Could not find language input or tags container.")
      return
    }

    const language = languageInput.value.trim()
    if (!language) {
      console.log("Empty language value")
      return
    }

    const normalized = language.toLowerCase()
    const existingValues = Array.from(
      languageTags.querySelectorAll("input[name='user[spoken_languages][]']")
    ).map((input) => input.value.toLowerCase())

    if (existingValues.includes(normalized)) {
      console.log("Language already exists:", normalized)
      languageInput.value = ""
      return
    }

    console.log("Adding language:", language)
    const wrapper = document.createElement("span")
    wrapper.className = "language-tag-modal"
    wrapper.innerHTML = `
      <span>${this.formatLabel(language)}</span>
      <button type="button" class="language-tag-modal-remove" aria-label="Remove language">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6L6 18M6 6l12 12"></path>
        </svg>
      </button>
      <input type="hidden" name="user[spoken_languages][]" value="${language}">
    `
    languageTags.appendChild(wrapper)
    languageInput.value = ""
    console.log("Language added successfully")
  }

  appendSkillTagDirect(skill, container) {
    console.log("appendSkillTagDirect called with:", skill, container)

    const existingValues = Array.from(container.querySelectorAll("input[name='user[tags][]']")).map(
      (input) => input.value
    )
    console.log("Existing skills:", existingValues)

    if (existingValues.includes(skill)) {
      console.log("Skill already exists:", skill)
      return
    }

    const wrapper = document.createElement("span")
    wrapper.className = "skill-tag-modal"
    wrapper.innerHTML = `
      <span>${this.formatLabel(skill)}</span>
      <button type="button" class="skill-tag-modal-remove" aria-label="Remove skill">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6L6 18M6 6l12 12"></path>
        </svg>
      </button>
      <input type="hidden" name="user[tags][]" value="${skill}">
    `
    container.appendChild(wrapper)
    console.log("Skill tag appended successfully")
  }

  // === Helpers ===
  formatLabel(value) {
    return value
      .split(/[\s_-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  // NUCLEAR OPTION: Direct event listeners that bypass everything
  setupDirectEventListeners() {
    console.log('Setting up DIRECT event listeners')

    // Remove profiler every 500ms
    setInterval(() => {
      document.querySelectorAll('.profiler-button, .profiler-totals, [class*="profiler"]').forEach(el => {
        el.remove()
      })
    }, 500)

    // Direct listener for toggleActions button - highest priority
    const toggleActionsBtn = document.querySelector('[data-profile-edit-target="actionsButton"]')
    if (toggleActionsBtn) {
      console.log('Found toggleActions button, adding direct listener')
      toggleActionsBtn.addEventListener('click', (e) => {
        console.log('DIRECT toggleActions clicked')
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        this.toggleActions(e)
      }, true) // Use capture phase
    } else {
      console.log('toggleActions button not found in setupDirectEventListeners')
    }

    // Direct listener for experience-view-more-btn - bypass global listener
    const experienceViewMoreBtn = document.querySelector('.experience-view-more-btn')
    if (experienceViewMoreBtn) {
      console.log('Found experience-view-more-btn, adding direct listener')
      experienceViewMoreBtn.addEventListener('click', (e) => {
        console.log('DIRECT experience-view-more clicked')
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        // Find the experience-toggle controller and call its toggle method
        const experienceController = this.application.getControllerForElementAndIdentifier(experienceViewMoreBtn.closest('[data-controller*="experience-toggle"]') || experienceViewMoreBtn.closest('[data-controller]'), 'experience-toggle')
        if (experienceController) {
          experienceController.toggle(e)
        }
      }, true) // Use capture phase
    } else {
      console.log('experience-view-more-btn not found in setupDirectEventListeners')
    }

    // Direct listener for experience-toggle#toggleDetails - bypass global listener
    const experienceToggleDetailsElements = document.querySelectorAll('[data-action*="experience-toggle#toggleDetails"]')
    if (experienceToggleDetailsElements.length > 0) {
      console.log('Found experience-toggle#toggleDetails elements, adding direct listeners')
      experienceToggleDetailsElements.forEach(element => {
        element.addEventListener('click', (e) => {
          console.log('DIRECT experience-toggle#toggleDetails clicked')
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          // Find the experience-toggle controller and call its toggleDetails method
          const experienceController = this.application.getControllerForElementAndIdentifier(element.closest('[data-controller*="experience-toggle"]') || element.closest('[data-controller]'), 'experience-toggle')
          if (experienceController) {
            experienceController.toggleDetails(e)
          }
        }, true) // Use capture phase
        element.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            console.log('DIRECT experience-toggle#toggleDetails keydown')
            e.preventDefault()
            e.stopPropagation()
            e.stopImmediatePropagation()
            const experienceController = this.application.getControllerForElementAndIdentifier(element.closest('[data-controller*="experience-toggle"]') || element.closest('[data-controller]'), 'experience-toggle')
            if (experienceController) {
              experienceController.toggleDetails(e)
            }
          }
        }, true) // Use capture phase
      })
    } else {
      console.log('experience-toggle#toggleDetails elements not found in setupDirectEventListeners')
    }
    // Add click listener to entire document
    //
    // IMPORTANT: each selector below MUST be scoped to `profile-edit#…`
    // (the Stimulus controller identifier). The previous version used
    // `[data-action*="closeModal"]` which matched ANY closeModal action on
    // the page — including foreign controllers like
    // `click->linkedin-import#closeModal` on the LinkedIn import modal's
    // X / Cancel buttons. We then preventDefault'd the event and dispatched
    // it to profile-edit's own closeModal, which only knows how to close
    // `.profile-modal` elements and silently no-ops on the LinkedIn modal.
    // Net result: X and Cancel did nothing.
    document.addEventListener('click', (e) => {
      const target = e.target.closest('[data-action*="profile-edit#closeModal"]')
      if (target) {
        console.log('DIRECT LISTENER: Close button clicked!')
        e.preventDefault()
        e.stopPropagation()
        // Create new event with correct currentTarget
        const newEvent = new Event('click', { bubbles: true, cancelable: true })
        Object.defineProperty(newEvent, 'currentTarget', { value: target, writable: false })
        this.closeModal(newEvent)
        return false
      }

      const addAlertBtn = e.target.closest('[data-action*="profile-edit#addAlert"]')
      if (addAlertBtn) {
        console.log('DIRECT LISTENER: Add alert button clicked!')
        e.preventDefault()
        e.stopPropagation()
        // Create new event with correct currentTarget
        const newEvent = new Event('click', { bubbles: true, cancelable: true })
        Object.defineProperty(newEvent, 'currentTarget', { value: addAlertBtn, writable: false })
        this.addAlert(newEvent)
        return false
      }

      const removeAlertBtn = e.target.closest('[data-action*="profile-edit#removeAlert"]')
      if (removeAlertBtn) {
        console.log('DIRECT LISTENER: Remove alert button clicked!')
        e.preventDefault()
        e.stopPropagation()
        // Create new event with correct currentTarget
        const newEvent = new Event('click', { bubbles: true, cancelable: true })
        Object.defineProperty(newEvent, 'currentTarget', { value: removeAlertBtn, writable: false })
        this.removeAlert(newEvent)
        return false
      }
    }, true) // Use capture phase
  }

  switchTab(event) {
    const tabName = event.currentTarget.dataset.tab
    if (!tabName) return

    // Update buttons
    this.tabTargets.forEach(tab => {
      tab.classList.toggle("profile-tab-button--active", tab.dataset.tab === tabName)
      tab.classList.toggle("profile-tab-button--inactive", tab.dataset.tab !== tabName)
    })

    // Update content
    this.tabContentTargets.forEach(content => {
      if (content.dataset.tab === tabName) {
        content.classList.remove("is-hidden")
      } else {
        content.classList.add("is-hidden")
      }
    })
  }

  toggleAutobot(event) {
    const enabled = event.target.checked
    const formData = new FormData()
    formData.append("user[autobot_enabled]", enabled)

    fetch("/users", {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      },
      body: formData
    })
      .then(response => {
        if (response.ok) {
          // Optionially update UI indicator
          const indicator = document.querySelector(".bot-status-indicator")
          const statusText = document.querySelector(".bot-status-text")
          if (indicator) {
            indicator.classList.toggle("bot-status-indicator--active", enabled)
            indicator.classList.toggle("bot-status-indicator--inactive", !enabled)
          }
          if (statusText) {
            statusText.textContent = enabled ? "Autobot is Active" : "Autobot is Paused"
          }
        } else {
          event.target.checked = !enabled
          alert("Failed to update autobot status.")
        }
      })
  }

  // === Autobot Keywords ===
  addKeyword(event) {
    if (event.type === 'keydown' && event.key !== 'Enter') return
    event.preventDefault()

    const input = this.hasKeywordInputTarget ? this.keywordInputTarget : this.element.querySelector('[data-profile-edit-target="keywordInput"]')
    const container = this.hasKeywordTagsTarget ? this.keywordTagsTarget : this.element.querySelector('[data-profile-edit-target="keywordTags"]')

    if (!input || !container) return

    const value = input.value.trim()
    if (!value) return

    this.createKeywordTag(value)
    input.value = ''
    this.syncKeywords()
    // Re-filter suggestions to remove the added one
    this.filterSuggestions()
  }

  createKeywordTag(value) {
    const container = this.hasKeywordTagsTarget ? this.keywordTagsTarget : this.element.querySelector('[data-profile-edit-target="keywordTags"]')
    const input = this.hasKeywordInputTarget ? this.keywordInputTarget : this.element.querySelector('[data-profile-edit-target="keywordInput"]')
    if (!container || !input) return

    // Duplicate check
    const existing = Array.from(container.querySelectorAll('.keyword-tag')).map(el => el.firstChild.textContent.trim().toLowerCase())
    if (existing.includes(value.toLowerCase())) return

    const tag = document.createElement('div')
    tag.className = "keyword-tag inline-flex items-center gap-1 rounded bg-[#F3F3F3] border border-[#191A23]/5 px-2 py-1 text-xs font-medium text-[#191A23]"
    tag.innerHTML = `
      ${value}
      <button type="button" class="ml-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full text-[#191A23]/40 hover:bg-[#191A23]/10 hover:text-[#191A23] transition-colors" data-action="click->profile-edit#removeKeyword">
          <span class="iconify" data-icon="lucide:x" data-width="10"></span>
      </button>
    `
    container.insertBefore(tag, input)
  }

  // === Suggestions Logic ===

  get aiKeywords() {
    return [
      "Generative AI", "LLM", "Machine Learning", "Deep Learning",
      "Natural Language Processing", "NLP", "Computer Vision", "Reinforcement Learning",
      "AI Engineer", "ML Engineer", "Data Scientist", "Prompt Engineering", "AI Research",
      "PyTorch", "TensorFlow", "HuggingFace", "LangChain", "OpenAI", "Stable Diffusion",
      "Robotics", "Ethical AI", "MLOps", "Fine-tuning", "RAG", "Vector Databases",
      "Python", "Rust", "Go", "C++", "Java", "React", "Next.js", "Ruby on Rails",
      "Data Analysis", "Big Data", "Neural Networks", "Chatbots", "Automation"
    ]
  }

  showSuggestions() {
    this.filterSuggestions()
    if (this.hasSuggestionsListTarget) {
      this.suggestionsListTarget.classList.remove('hidden')
    }
  }

  hideSuggestions() {
    setTimeout(() => {
      if (this.hasSuggestionsListTarget) {
        this.suggestionsListTarget.classList.add('hidden')
      }
    }, 200)
  }

  filterSuggestions() {
    if (!this.hasSuggestionsListTarget || !this.hasKeywordInputTarget) return

    const inputVal = this.keywordInputTarget.value.toLowerCase()
    const all = this.aiKeywords

    // Check existing tags
    const container = this.hasKeywordTagsTarget ? this.keywordTagsTarget : this.element.querySelector('[data-profile-edit-target="keywordTags"]')
    const added = Array.from(container.querySelectorAll('.keyword-tag')).map(el => el.firstChild.textContent.trim().toLowerCase())

    let matches = all.filter(k =>
      !added.includes(k.toLowerCase()) &&
      k.toLowerCase().includes(inputVal)
    )

    this.renderSuggestions(matches)
  }

  renderSuggestions(items) {
    if (items.length === 0) {
      // Optional: Don't show anything or show 'No matches'
      this.suggestionsListTarget.classList.add('hidden')
      return
    }

    this.suggestionsListTarget.classList.remove('hidden')
    this.suggestionsListTarget.innerHTML = items.map(item => `
      <div class="cursor-pointer px-3 py-2 text-xs text-[#191A23] hover:bg-[#F3F3F3] transition-colors"
           data-action="click->profile-edit#selectSuggestion"
           data-value="${item}">
        ${item}
      </div>
    `).join('')
  }

  selectSuggestion(event) {
    const value = event.currentTarget.dataset.value
    this.createKeywordTag(value)
    this.keywordInputTarget.value = ''
    this.syncKeywords()
    this.suggestionsListTarget.classList.add('hidden')
    this.keywordInputTarget.focus()
  }

  removeKeyword(event) {
    event.preventDefault()
    event.target.closest('.keyword-tag').remove()
    this.syncKeywords()
  }

  toggleLocation(event) {
    const card = event.currentTarget
    const value = card.dataset.value
    const hidden = this.hasLocationHiddenTarget ? this.locationHiddenTarget : this.element.querySelector('[data-profile-edit-target="locationHidden"]')

    if (!hidden) return

    let current = hidden.value ? hidden.value.split(',').map(s => s.trim()) : []

    // Normalize logic
    if (current.includes(value)) {
      current = current.filter(v => v !== value)
      // Visuals: Inactive
      card.classList.remove('border-[#191A23]', 'bg-[#F3F3F3]')
      card.classList.add('border-[#191A23]/10', 'bg-white')
      const check = card.querySelector('.check-indicator')
      if (check) check.classList.add('scale-0', 'opacity-0')
    } else {
      current.push(value)
      // Visuals: Active
      card.classList.add('border-[#191A23]', 'bg-[#F3F3F3]')
      card.classList.remove('border-[#191A23]/10', 'bg-white')
      const check = card.querySelector('.check-indicator')
      if (check) check.classList.remove('scale-0', 'opacity-0')
    }

    hidden.value = current.join(', ')
    this.updateSettings({ target: hidden })
  }

  syncKeywords() {
    const container = this.hasKeywordTagsTarget ? this.keywordTagsTarget : this.element.querySelector('[data-profile-edit-target="keywordTags"]')
    const hidden = this.hasKeywordHiddenTarget ? this.keywordHiddenTarget : this.element.querySelector('[data-profile-edit-target="keywordHidden"]')

    if (container && hidden) {
      const keywords = Array.from(container.querySelectorAll('.keyword-tag')).map(el => el.firstChild.textContent.trim())
      hidden.value = keywords.join(', ')

      // Trigger update
      this.updateSettings({ target: hidden })
    }
  }

  updateSettings(event) {
    const field = event.target.dataset.field
    const value = event.target.value

    const formData = new FormData()
    // For nested jsonb, we'll send it as a nested param or handle it in the controller
    // Simplest is to send the whole settings object or specific fields
    formData.append(`user[autobot_settings][${field}]`, value)

    fetch("/users", {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      },
      body: formData
    })
      .then(response => {
        if (!response.ok) {
          alert("Failed to update settings.")
        }
      })
  }

  async runTestRun(event) {
    if (event) event.preventDefault();
    const button = event.currentTarget;
    const originalContent = button.innerHTML;

    button.disabled = true;
    button.innerHTML = `<iconify-icon icon="lucide:loader-2" class="animate-spin" width="12"></iconify-icon> Testing...`;

    try {
      const response = await fetch('/autobot/test_run', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        this.displayTestResults(data.report);
      } else {
        alert(data.error || "Failed to run test.");
      }
    } catch (error) {
      console.error("Test run error:", error);
      alert("An unexpected error occurred.");
    } finally {
      button.disabled = false;
      button.innerHTML = originalContent;
    }
  }

  displayTestResults(report) {
    const resultsDiv = document.getElementById('test-run-results');
    const contentDiv = document.getElementById('test-run-content');

    if (!resultsDiv || !contentDiv) return;

    resultsDiv.classList.remove('hidden');

    let html = `
      <div class="space-y-4">
        <div class="flex items-center gap-3">
          <div class="px-2 py-1 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold">
            ${report.candidate_jobs_count} Jobs Found
          </div>
          <div class="px-2 py-1 rounded bg-[#b8ff66] text-gray-900 text-[10px] font-bold">
            ${report.matches.length} Matches
          </div>
        </div>
    `;

    if (report.matches.length > 0) {
      html += `<div class="space-y-2">`;
      report.matches.slice(0, 3).forEach(job => {
        html += `
          <div class="p-3 rounded-lg bg-white border border-emerald-100 flex items-center justify-between">
            <div class="flex flex-col">
              <span class="text-xs font-bold text-gray-800">${job.title}</span>
              <span class="text-[10px] text-gray-400 font-medium">${job.company} • ${job.location || 'Remote'}</span>
            </div>
            <div class="flex items-center gap-1.5">
              <div class="size-6 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 text-[10px] font-bold">
                ${job.score}
              </div>
              <iconify-icon icon="lucide:check-circle" class="text-emerald-500" width="14"></iconify-icon>
            </div>
          </div>
        `;
      });
      if (report.matches.length > 3) {
        html += `<p class="text-[9px] text-center text-emerald-600 font-medium">+ ${report.matches.length - 3} more matches</p>`;
      }
      html += `</div>`;
    }

    if (report.recommendations.length > 0) {
      html += `
        <div class="p-3 rounded-lg bg-amber-50 border border-amber-100">
          <p class="text-[10px] font-bold text-amber-600 mb-1 uppercase">Recommendations</p>
          <ul class="list-disc list-inside space-y-1">
            ${report.recommendations.map(r => `<li class="text-[10px] text-amber-700 font-medium">${r}</li>`).join('')}
          </ul>
        </div>
      `;
    }

    html += `</div>`;
    contentDiv.innerHTML = html;

    // Auto-scroll to results
    resultsDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  async toggleAutobot(event) {
    const checked = event.target.checked;

    try {
      const response = await fetch('/autobot/update_status', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: checked })
      });

      const data = await response.json();
      if (!data.success) {
        event.target.checked = !checked;
        alert(data.error || "Failed to update status");
      } else {
        // Refresh page to show updated status UI
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating autobot status:', error);
      event.target.checked = !checked;
      alert("Failed to update status");
    }
  }

  // === Focus Management ===
  trapFocus(modal) {
    if (!modal) return;

    // Get all focusable elements within modal
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      // Focus first element
      focusableElements[0].focus();
    }

    // Add keydown listener for tab navigation
    this.focusTrapHandler = (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift + Tab - go to previous
          if (document.activeElement === firstElement) {
            lastElement.focus();
          } else {
            const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);
            if (currentIndex > 0) {
              focusableElements[currentIndex - 1].focus();
            }
          }
        } else {
          // Tab - go to next
          if (document.activeElement === lastElement) {
            firstElement.focus();
          } else {
            const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);
            if (currentIndex < focusableElements.length - 1) {
              focusableElements[currentIndex + 1].focus();
            }
          }
        }
      }
    };

    // Ensure links in modal are clickable
    this.modalClickHandler = (e) => {
      // Allow all clicks within modal dialog
      const dialog = e.target.closest('.profile-modal__dialog');
      if (dialog) {
        // This is a click within the modal dialog - allow it
        console.log('Click allowed in modal dialog:', e.target);
        return;
      }

      // Check if clicking on backdrop (close modal)
      if (e.target.classList.contains("profile-modal")) {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal(e);
        return;
      }

      // Allow links anywhere in modal
      const link = e.target.closest('a[href]');
      if (link && e.target.closest('.profile-modal')) {
        console.log('Link clicked in modal:', link.href);
        // Let the link work normally - don't prevent default
        return;
      }
    };

    document.addEventListener('keydown', this.focusTrapHandler);
    modal.addEventListener('click', this.modalClickHandler);
  }

  removeFocusTrap(modal) {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
    if (this.modalClickHandler) {
      document.removeEventListener('click', this.modalClickHandler);
      this.modalClickHandler = null;
    }
  }
}
